import { statusAPI } from '../api/client';
import { signRequestWithKey } from '../crypto/signing';
import { useAuthStore, type Device, type Identity } from '../store/authStore';
import { useVaultStore } from '../store/vaultStore';
import { serverKeyFromServerUrl } from '../utils/serverKey';

export const PRESENCE_HEARTBEAT_INTERVAL_MS = 30_000;
export const PRESENCE_STATUS_REFRESH_INTERVAL_MS = 30_000;
const TARGETED_PRESENCE_REFRESH_DEBOUNCE_MS = 15_000;

const targetedPresenceRefreshLastAt = new Map<string, number>();
const targetedPresenceRefreshInFlight = new Map<string, Promise<void>>();

function findDeviceForIdentity(identityId: string): Device | null {
  const state = useAuthStore.getState();
  const current = state.currentDeviceId ? state.devices[state.currentDeviceId] : null;
  if (current?.identityId === identityId) return current;
  return Object.values(state.devices).find((device) => device.identityId === identityId) || null;
}

function getIdentityServerKey(identity: Identity): string {
  return serverKeyFromServerUrl(identity.serverUrl) || identity.serverId || '';
}

function getContactIdentityServerKey(contactIdentity: { serverId?: string; serverUrl?: string }): string {
  return serverKeyFromServerUrl(contactIdentity.serverUrl) || contactIdentity.serverId || '';
}

function chunk<T>(items: T[], size: number): T[][] {
  const chunks: T[][] = [];
  for (let i = 0; i < items.length; i += size) {
    chunks.push(items.slice(i, i + size));
  }
  return chunks;
}

export async function sendPresenceHeartbeatAllCircles(): Promise<void> {
  const identities = Object.values(useAuthStore.getState().identities);

  await Promise.allSettled(identities.map(async (identity) => {
    if (!identity.serverUrl) return;
    const device = findDeviceForIdentity(identity.identityId);
    if (!device) return;

    const signed = await signRequestWithKey(
      'status:heartbeat',
      {},
      device.deviceId,
      device.signingKey
    );
    await statusAPI.heartbeat(signed, identity.serverUrl);
  }));
}

export async function refreshContactPresenceStatuses(): Promise<void> {
  const authState = useAuthStore.getState();
  const vault = useVaultStore.getState().vaultData;
  if (!vault) return;

  const contactIdentitiesByServerKey = new Map<string, Set<string>>();
  for (const contact of Object.values(vault.contacts || {})) {
    for (const contactIdentity of contact.identities || []) {
      const serverKey = getContactIdentityServerKey(contactIdentity);
      const identityId = (contactIdentity.identityId || '').trim();
      if (!serverKey || !identityId) continue;
      const set = contactIdentitiesByServerKey.get(serverKey) || new Set<string>();
      set.add(identityId);
      contactIdentitiesByServerKey.set(serverKey, set);
    }
  }

  const collected: Record<string, {
    statusText: string | null;
    statusUpdatedAt: string | null;
    avatarBlobId?: string | null;
    presence?: {
      visibility?: 'visible' | 'hidden';
      isOnline: boolean;
      lastSeenAt: string | null;
      onlineUntil: string | null;
    };
  }> = {};

  const tasks = Object.values(authState.identities).map(async (identity) => {
    if (!identity.serverUrl) return;
    const serverKey = getIdentityServerKey(identity);
    const identityIds = Array.from(contactIdentitiesByServerKey.get(serverKey) || []);
    if (identityIds.length === 0) return;

    const device = findDeviceForIdentity(identity.identityId);
    if (!device) return;

    for (const batch of chunk(identityIds, 100)) {
      const signed = await signRequestWithKey(
        'status:get',
        { identityIds: batch },
        device.deviceId,
        device.signingKey
      );
      const response = await statusAPI.get(signed, identity.serverUrl);
      if (response.status !== 'ok' || !response.result?.statuses) continue;
      Object.assign(collected, response.result.statuses);
    }
  });

  await Promise.allSettled(tasks);

  if (Object.keys(collected).length > 0) {
    useVaultStore.getState().updateContactStatuses(collected);
  }
}

function resolvePresenceRefreshContext(
  identityId: string,
  options?: {
    localIdentityId?: string;
    serverUrl?: string;
  }
): { localIdentityId: string; serverUrl?: string } | null {
  const authState = useAuthStore.getState();
  const requestedLocalIdentityId = (options?.localIdentityId || '').trim();
  if (requestedLocalIdentityId) {
    const localIdentity = authState.identities[requestedLocalIdentityId];
    if (!localIdentity) return null;
    return {
      localIdentityId: requestedLocalIdentityId,
      serverUrl: options?.serverUrl || localIdentity.serverUrl
    };
  }

  const vault = useVaultStore.getState().vaultData;
  if (!vault) return null;

  let targetServerKey = '';
  for (const contact of Object.values(vault.contacts || {})) {
    const contactIdentity = (contact.identities || []).find((entry) => (entry.identityId || '').trim() === identityId);
    if (contactIdentity) {
      targetServerKey = getContactIdentityServerKey(contactIdentity);
      break;
    }
  }
  if (!targetServerKey) return null;

  const localIdentity = Object.values(authState.identities).find((entry) => getIdentityServerKey(entry) === targetServerKey);
  if (!localIdentity) return null;
  return {
    localIdentityId: localIdentity.identityId,
    serverUrl: options?.serverUrl || localIdentity.serverUrl
  };
}

export async function refreshPresenceForIdentity(
  identityId: string,
  options?: {
    localIdentityId?: string;
    serverUrl?: string;
    debounceMs?: number;
  }
): Promise<void> {
  const targetIdentityId = (identityId || '').trim();
  if (!targetIdentityId) return;

  const context = resolvePresenceRefreshContext(targetIdentityId, options);
  if (!context?.serverUrl) return;

  const debounceMs = Math.max(0, options?.debounceMs ?? TARGETED_PRESENCE_REFRESH_DEBOUNCE_MS);
  const key = `${context.localIdentityId}:${context.serverUrl}:${targetIdentityId}`;
  const now = Date.now();
  const lastAt = targetedPresenceRefreshLastAt.get(key) || 0;
  if (debounceMs > 0 && now - lastAt < debounceMs) return;

  const existing = targetedPresenceRefreshInFlight.get(key);
  if (existing) return existing;

  targetedPresenceRefreshLastAt.set(key, now);
  const task = (async () => {
    const device = findDeviceForIdentity(context.localIdentityId);
    if (!device) return;

    const signed = await signRequestWithKey(
      'status:get',
      { identityIds: [targetIdentityId] },
      device.deviceId,
      device.signingKey
    );
    const response = await statusAPI.get(signed, context.serverUrl);
    if (response.status !== 'ok' || !response.result?.statuses) return;
    useVaultStore.getState().updateContactStatuses(response.result.statuses);
  })().finally(() => {
    targetedPresenceRefreshInFlight.delete(key);
  });

  targetedPresenceRefreshInFlight.set(key, task);
  return task;
}

export function isPresenceOnline(presence: { isOnline?: boolean; onlineUntil?: string | null } | null | undefined, nowMs = Date.now()): boolean {
  if (!presence) return false;
  if ((presence as { visibility?: string }).visibility === 'hidden') return false;
  const onlineUntil = presence.onlineUntil ? Date.parse(presence.onlineUntil) : Number.NaN;
  if (Number.isFinite(onlineUntil)) return onlineUntil > nowMs;
  return presence.isOnline === true;
}

export function getLatestPresence(statuses: Array<{
  presence?: {
    visibility?: 'visible' | 'hidden';
    isOnline: boolean;
    lastSeenAt: string | null;
    onlineUntil: string | null;
  };
} | null | undefined>, nowMs = Date.now()): {
  isOnline: boolean;
  lastSeenAt: string | null;
  hasHidden: boolean;
} {
  let latest: string | null = null;
  let latestMs = 0;
  let hasHidden = false;

  for (const status of statuses) {
    const presence = status?.presence;
    if (!presence) continue;
    if (presence.visibility === 'hidden') {
      hasHidden = true;
      continue;
    }
    if (isPresenceOnline(presence, nowMs)) {
      return {
        isOnline: true,
        lastSeenAt: presence.lastSeenAt,
        hasHidden
      };
    }
    const seenMs = presence.lastSeenAt ? Date.parse(presence.lastSeenAt) : Number.NaN;
    if (Number.isFinite(seenMs) && seenMs > latestMs) {
      latestMs = seenMs;
      latest = presence.lastSeenAt;
    }
  }

  return {
    isOnline: false,
    lastSeenAt: latest,
    hasHidden
  };
}
